import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

const consumerPlans = [
  { key: "Free", label: "Free", price: "$0", tag: "1 plant", perks: ["1 plant profile", "2-year milestone calendar", "Smart watering reminders", "Pest & disease alert library"] },
  { key: "Pro", label: "Pro", price: "$9", tag: "Up to 10 plants", featured: true, perks: ["Up to 10 plant profiles", "Priority pest & disease alerts", "Rainfall-aware watering", "Assigned landcare crew"] },
  { key: "Orchardist", label: "Orchardist", price: "$19", tag: "Unlimited + AI", perks: ["Unlimited plant profiles", "Advanced AI leaf diagnosis", "Species pest reference comparison", "Everything in Pro"] },
];

const providerPlans = [
  { key: "CrewBasic", label: "Crew Basic", price: "$49", tag: "1 crew", perks: ["1 crew seat", "Job board & checklists", "Milestone dispatch", "Client management"] },
  { key: "CrewPro", label: "Crew Pro", price: "$149", tag: "3 crews", featured: true, perks: ["Up to 3 crew seats", "AI leaf diagnosis on tickets", "Everything in Crew Basic"] },
  { key: "CrewEnterprise", label: "Crew Enterprise", price: "$399", tag: "White-Label", perks: ["Unlimited crew seats", "White-label branding (logo, color, email sender)", "Custom SLAs & API", "Everything in Crew Pro"] },
];

export default function Pricing() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  // Poll payment status when returning from Stripe (?session_id=...)
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          updateUser({ plan: data.plan });
          toast.success(`Payment successful — you're now on the ${data.plan} plan`);
          window.history.replaceState({}, "", "/pricing");
          return;
        }
        if (data.status === "expired") { toast.error("Payment session expired."); return; }
      } catch (e) { /* keep polling */ }
      if (attempts++ < 6) setTimeout(poll, 2000);
      else toast.message("Still processing — check back shortly.");
    };
    toast.message("Confirming your payment…");
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = async (plan) => {
    if (!user) { navigate("/auth"); return; }
    if (plan === "Free") {
      await api.post("/plan", { plan });
      updateUser({ plan });
      toast.success("Switched to the Free plan");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  const Card = ({ p }) => (
    <div data-testid={`plan-${p.key}`} className={`relative rounded-3xl border p-8 flex flex-col ${p.featured ? "border-emerald-700 bg-emerald-950 text-white shadow-[0_20px_60px_rgba(6,78,59,0.25)]" : "border-black/5 bg-white"}`}>
      {p.tag && <span className={`absolute -top-3 left-8 text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full ${p.featured ? "bg-amber-400 text-emerald-950" : "bg-emerald-100 text-emerald-800"}`}>{p.tag}</span>}
      <h3 className={`font-serif text-3xl ${p.featured ? "text-white" : "text-emerald-950"}`}>{p.label}</h3>
      <div className="mt-3"><span className="font-serif text-4xl">{p.price}</span><span className={`text-sm ${p.featured ? "text-emerald-200" : "text-stone-400"}`}>/mo</span></div>
      <ul className="mt-6 space-y-3 flex-1">
        {p.perks.map((k) => (
          <li key={k} className={`flex items-start gap-2 text-sm ${p.featured ? "text-emerald-50" : "text-stone-600"}`}>
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-amber-300" : "text-emerald-600"}`} />{k}
          </li>
        ))}
      </ul>
      <Button data-testid={`choose-${p.key}`} onClick={() => choose(p.key)} disabled={busy}
        className={`mt-8 rounded-full h-11 ${p.featured ? "bg-amber-400 text-emerald-950 hover:bg-amber-300" : "bg-emerald-900 text-white hover:bg-emerald-800"}`}>
        {user?.plan === p.key ? "Current plan" : p.key === "Free" ? "Choose" : busy ? "Redirecting…" : "Subscribe"}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <span className="text-xs uppercase tracking-[0.3em] text-emerald-700">Pricing</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-emerald-950">Plans for growers & crews</h1>
        <p className="mt-3 text-stone-500 max-w-xl">Homeowners nurture their trees. Landcare crews scale their operations. Secure checkout powered by Stripe. Payments are encrypted and protected.</p>

        <h2 className="mt-14 font-serif text-2xl text-emerald-950">For Homeowners</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6 items-stretch">
          {consumerPlans.map((p) => <Card key={p.name} p={p} />)}
        </div>

        <h2 className="mt-16 font-serif text-2xl text-emerald-950">For Landcare Crews (B2B)</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          {providerPlans.map((p) => <Card key={p.name} p={p} />)}
        </div>
      </div>
    </div>
  );
}
